<template>
  <form
    class="offer-revision-form uk-form-stacked"
    @submit.prevent="submit"
  >
    <h2 class="uk-margin-medium">
      <router-link :to="{ name: 'offer', params: { id: offer.id } }">
        {{offer.name}}
      </router-link>
    </h2>

    <!-- Validity -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">
        {{$t('fieldsets.validity')}}
      </legend>
      <div uk-grid class="uk-grid-small">
        <div class="uk-width-1-4">
          <date-time-picker
            :label="$t('fields.validFrom')"
            v-model="fields.values.validFrom"
            :errors="fields.errors.validFrom"
            :max="fields.values.validUntil"
            :disabled="!isEditable"
            placeholder="∞"
          />
        </div>
        <div class="uk-width-1-4">
          <date-time-picker
            :label="$t('fields.validUntil')"
            v-model="fields.values.validUntil"
            :errors="fields.errors.validUntil"
            :min="fields.values.validFrom"
            :disabled="!isEditable"
            placeholder="∞"
          />
        </div>
      </div>
    </fieldset>

    <!-- Prices -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">
        {{$t('fieldsets.prices')}}
      </legend>

      <inheritance-toggle
        v-model="fields.values.prices"
        toggle-label="Überschreiben"
        :overridden-default="[]"
      >
        <price-manager
          v-model="fields.values.prices"
          :errors="fields.errors.prices"
          :disabled="!isEditable"
        />
        <price-manager
          slot="inherited"
          :value="offer.prices"
          :has-controls="false"
        />
      </inheritance-toggle>
    </fieldset>

    <!-- Publications -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">
        {{$t('fieldsets.publications')}}
      </legend>

      <inheritance-toggle
        v-model="fields.values.publications"
        toggle-label="Überschreiben"
        :overridden-default="[]"
      >
        <publication-manager
          v-model="fields.values.publications"
          :errors="fields.errors.publications"
          :disabled="!isEditable"
        />
        <publication-manager
          slot="inherited"
          :value="offer.publications"
          :has-controls="false"
        />
      </inheritance-toggle>
    </fieldset>

    <!-- Details -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.details')}}</legend>

      <text-input
        type="number"
        :label="$t('fields.maxOrderableAmount')"
        v-model.number="fields.values.maxOrderableAmount"
        :errors="fields.errors.maxOrderableAmount"
        :disabled="!isEditable"
        :placeholder="offer.maxOrderableAmount"
        class="uk-width-1-4"
      />

      <text-list-input
        :label="$t('fields.usps')"
        v-model="fields.values.usps"
        :errors="fields.errors.usps"
        :disabled="!isEditable"
        :placeholder="offer.usps"
      />

      <text-list-input
        :label="$t('fields.summaryTexts')"
        v-model="fields.values.summaryTexts"
        :errors="fields.errors.summaryTexts"
        :disabled="!isEditable"
        :placeholder="offer.summaryTexts"
      />
    </fieldset>

    <!-- Media -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">
        {{$t('fieldsets.media')}}
      </legend>

      <attachment-manager
        attachment-type="MEDIA_OBJECT"
        v-model="fields.values.media"
        :errors="fields.errors.media"
        :disabled="!isEditable"
      />
    </fieldset>

    <div class="uk-margin-medium-top">
      <submit-button type="primary" :disabled="!isSubmittable" />
      <cancel-button />
    </div>
  </form>
</template>

<script>
import pick from 'lodash/pick';
import AttachmentManager from '@/components/AttachmentManager';
import CancelButton from '@/components/CancelButton';
import CommonButton from '@/components/CommonButton';
import DataGridActions from '@/components/DataGridActions';
import DateTimePicker from '@/components/DateTimePicker';
import Form from '@/mixins/Form';
import InheritanceToggle from '@/components/InheritanceToggle';
import PriceManager from '@/components/PriceManager';
import PublicationManager from '@/components/PublicationManager';
import ReadonlyFormField from '@/components/ReadonlyFormField';
import SubmitButton from '@/components/SubmitButton';
import TextListInput from '@/components/TextListInput';
import TextInput from '@/components/TextInput';
import TextPlaceholder from '@/components/TextPlaceholder';

export default {
  name: 'offer-revision-form',
  mixins: [
    Form({
      maxOrderableAmount: true,
      media: {
        type: Form.Types.ReferenceList,
        default: null,
        dumpTo: 'mediaObjectIds',
      },
      prices: {
        type: Form.Types.NullableArray,
        dump(prices) {
          if (!prices) return null;
          return prices.map((price) => {
            const newPrice = pick(price, ['type', 'paymentFrequency', 'value']);
            if (price.organization) {
              newPrice.organizationId = price.organization.id;
            } else {
              newPrice.organizationId = null;
            }
            delete newPrice.organization;
            return newPrice;
          });
        },
      },
      publications: {
        type: Form.Types.NullableArray,
        dump(publications) {
          if (!publications) return null;
          return publications.map((publication) => {
            const newPublication = pick(publication, ['list', 'position']);
            if (publication.tenant) {
              newPublication.tenantId = publication.tenant.id;
            } else {
              newPublication.tenantId = null;
            }
            delete newPublication.tenant;
            return newPublication;
          });
        },
      },
      summaryTexts: { type: Form.Types.NullableArray },
      usps: { type: Form.Types.NullableArray },
      validFrom: true,
      validUntil: true,
    }),
  ],
  components: {
    AttachmentManager,
    CancelButton,
    CommonButton,
    DataGridActions,
    DateTimePicker,
    InheritanceToggle,
    PriceManager,
    PublicationManager,
    ReadonlyFormField,
    SubmitButton,
    TextListInput,
    TextInput,
    TextPlaceholder,
  },
  props: {
    offer: {
      type: Object,
      required: true,
    },
  },
};
</script>



// WEBPACK FOOTER //
// OfferRevisionForm.vue?0f166290